/**
 * One field, rendered from its schema entry.
 *
 * The whole of the renderer's knowledge of the domain is the `kind` switch below. Adding a question
 * to any form touches no file in this directory.
 */

import { useMemo } from 'react';
import { PrefillBadge, ReferenceNote } from './PrefillBadge.tsx';
import { PlaceholderPhotoCapture, PlaceholderSerialCapture } from './capture-contract.tsx';
import type { PhotoCaptureProps, SerialCaptureProps } from './capture-contract.tsx';
import type { Problem } from '../validate.ts';
import type { Answer, Field, FieldEntry, PhotoRef, ReferenceValue, SerialValue } from '../types.ts';

export interface FieldControlProps {
  readonly field: Field;
  readonly entry: FieldEntry | undefined;
  readonly reference: ReferenceValue | undefined;
  readonly problems: readonly Problem[];
  readonly onChange: (value: Answer) => void;
  readonly onRevert: () => void;
  readonly storePhoto: (blob: Blob) => Promise<PhotoRef>;
  readonly onCaptured: (photo: PhotoRef) => void;
  readonly onRemovePhoto: (blobId: string) => void;
  /** Swap in the real components from `src/capture` here. */
  readonly PhotoCapture?: (props: PhotoCaptureProps) => JSX.Element;
  readonly SerialCapture?: (props: SerialCaptureProps) => JSX.Element;
}

function asStrings(value: Answer | undefined): readonly string[] {
  return Array.isArray(value) ? (value.filter((v) => typeof v === 'string') as readonly string[]) : [];
}

function asPhotos(value: Answer | undefined): readonly PhotoRef[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is PhotoRef => typeof v === 'object' && v !== null && 'blobId' in v);
}

export function FieldControl(props: FieldControlProps): JSX.Element {
  const {
    field,
    entry,
    reference,
    problems,
    onChange,
    onRevert,
    storePhoto,
    onCaptured,
    onRemovePhoto,
    PhotoCapture = PlaceholderPhotoCapture,
    SerialCapture = PlaceholderSerialCapture,
  } = props;

  const value = entry?.value;
  const invalid = problems.length > 0;
  const inferred = field.inferred === true;
  const describedBy = useMemo(() => {
    const ids: string[] = [];
    if (field.help !== undefined) ids.push(`${field.id}-help`);
    // The "inferred" caveat is a DESCRIPTION, not part of the question. See the label block below.
    if (inferred) ids.push(`${field.id}-inferred`);
    if (invalid) ids.push(`${field.id}-error`);
    return ids.length > 0 ? ids.join(' ') : undefined;
  }, [field.help, field.id, inferred, invalid]);

  if (field.kind === 'note') {
    return (
      <p className="fx-note" role="note">
        {field.label}
      </p>
    );
  }

  /**
   * THE ACCESSIBLE NAME IS THE QUESTION, AND NOTHING BUT THE QUESTION.
   * ================================================================
   *
   * The `<label>` below is not only the question: it also carries the required `*` and, on
   * reconstructed questions, an "inferred" pill. `*` is already `aria-hidden`, so it was already
   * excluded — but the pill was not, and every name computed from that label came out as
   * `"Which door is damaged?inferred"` (measured; note the missing space, which is how two
   * separate spans concatenate). Pointing `aria-labelledby` at a span holding ONLY the question
   * text gives every control the same name it would have had with no pill, and the caveat reaches
   * the same user through `aria-describedby` instead — where a caveat belongs.
   */
  const questionId = `${field.id}-question`;

  const shared = {
    id: field.id,
    'aria-labelledby': questionId,
    'aria-invalid': invalid,
    ...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {}),
    ...(field.required === true ? { 'aria-required': true } : {}),
  } as const;

  /**
   * Does the field render a single element carrying `id={field.id}`?
   *
   * Only the `shared`-spread branches below do (`textarea`, `number`, `date`, `time`, and the
   * `text`/default case). `yesno`, `single` and `multi` render a *group* of inputs; `photo` and
   * `serial` delegate to a capture component that owns its own markup. For those five,
   * `<label htmlFor={field.id}>` pointed at an element that DOES NOT EXIST — an association the
   * browser silently drops, so tapping the question focused nothing and the `<label>` was a
   * decorative block of text pretending to be a form label. Those kinds get a plain container
   * instead, and their group takes its name from `aria-labelledby` (which does resolve).
   */
  const controlOwnsFieldId = !['yesno', 'single', 'multi', 'photo', 'serial'].includes(field.kind);

  let control: JSX.Element;

  switch (field.kind) {
    case 'textarea':
      control = (
        <textarea
          {...shared}
          rows={4}
          value={typeof value === 'string' ? value : ''}
          {...(field.placeholder !== undefined ? { placeholder: field.placeholder } : {})}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;

    case 'number':
      control = (
        <input
          {...shared}
          type="number"
          inputMode="decimal"
          value={typeof value === 'number' ? String(value) : ''}
          {...(field.validate?.min !== undefined ? { min: field.validate.min } : {})}
          {...(field.validate?.max !== undefined ? { max: field.validate.max } : {})}
          {...(field.validate?.integer === true ? { step: 1 } : {})}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
      break;

    case 'date':
      control = (
        <input {...shared} type="date" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
      );
      break;

    case 'time':
      control = (
        <input
          {...shared}
          type="time"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
      break;

    case 'yesno':
      control = (
        <div className="fx-yesno" role="radiogroup" aria-labelledby={questionId}>
          {[true, false].map((option) => (
            <label key={String(option)} className={value === option ? 'fx-choice fx-choice-on' : 'fx-choice'}>
              <input
                type="radio"
                name={field.id}
                checked={value === option}
                onChange={() => onChange(option)}
                aria-invalid={invalid}
              />
              {option ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      );
      break;

    case 'single':
      control = (
        <div className="fx-single" role="radiogroup" aria-labelledby={questionId}>
          {(field.options ?? []).map((option) => (
            <label key={option} className={value === option ? 'fx-choice fx-choice-on' : 'fx-choice'}>
              <input
                type="radio"
                name={field.id}
                value={option}
                checked={value === option}
                onChange={() => onChange(option)}
                aria-invalid={invalid}
              />
              {option}
            </label>
          ))}
        </div>
      );
      break;

    case 'multi': {
      const chosen = asStrings(value);
      control = (
        <div className="fx-multi" role="group" aria-labelledby={questionId}>
          {(field.options ?? []).map((option) => {
            const on = chosen.includes(option);
            return (
              <label key={option} className={on ? 'fx-choice fx-choice-on' : 'fx-choice'}>
                <input
                  type="checkbox"
                  value={option}
                  checked={on}
                  onChange={() => onChange(on ? chosen.filter((c) => c !== option) : [...chosen, option])}
                  aria-invalid={invalid}
                />
                {option}
              </label>
            );
          })}
        </div>
      );
      break;
    }

    case 'photo':
      control = (
        <PhotoCapture
          fieldId={field.id}
          label={field.label}
          value={asPhotos(value)}
          {...(field.validate?.maxPhotos !== undefined ? { max: field.validate.maxPhotos } : {})}
          store={storePhoto}
          onCaptured={onCaptured}
          onRemove={onRemovePhoto}
        />
      );
      break;

    case 'serial': {
      const serial =
        typeof value === 'object' && value !== null && 'serial' in value ? (value as SerialValue) : undefined;
      const referenceSerial =
        reference && typeof reference.value === 'string' ? reference.value : undefined;
      control = (
        <SerialCapture
          fieldId={field.id}
          label={field.label}
          value={serial}
          reference={referenceSerial}
          onCaptured={onChange}
          {...(field.validate?.minLength !== undefined ? { minLength: field.validate.minLength } : {})}
        />
      );
      break;
    }

    default:
      control = (
        <input
          {...shared}
          type="text"
          value={typeof value === 'string' ? value : ''}
          {...(field.placeholder !== undefined ? { placeholder: field.placeholder } : {})}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }

  const labelBody = (
    <>
      <span id={questionId}>{field.label}</span>
      {field.required === true && (
        <span className="fx-required" aria-hidden="true">
          {' '}
          *
        </span>
      )}
      {inferred && (
        <span className="fx-inferred" aria-hidden="true">
          {' '}
          inferred
        </span>
      )}
    </>
  );

  return (
    <div className={invalid ? 'fx-field fx-field-invalid' : 'fx-field'} data-field-id={field.id}>
      {/* `${field.id}-label` is NO LONGER an accessible-name target — `questionId` is. It is kept only
          as a stable hook on the question block; nothing computes a name from it. */}
      {controlOwnsFieldId ? (
        <label id={`${field.id}-label`} htmlFor={field.id} className="fx-label">
          {labelBody}
        </label>
      ) : (
        /* No `<label>`: there is no single labelable control to point it at. See `controlOwnsFieldId`. */
        <div id={`${field.id}-label`} className="fx-label">
          {labelBody}
        </div>
      )}

      {/*
        THE "INFERRED" CAVEAT IS ON SCREEN, NOT IN A `title=`.
        =====================================================
        This used to be `title="Not from a source form — wording is inferred and under review"` on the
        pill. `title` surfaces on mouse hover and nowhere else: there is no hover on a touch screen, so
        on the app's only platform the explanation of what "inferred" means did not exist. The pill was
        a word with no definition — and what it is admitting is that the app may be asking the wrong
        question, which is exactly the caveat a technician disputing an answer later needs to have seen.

        Now it is a sentence, tied to the control by `aria-describedby`, and the pill itself is
        `aria-hidden` so the word is not announced twice.
      */}
      {inferred && (
        <p id={`${field.id}-inferred`} className="fx-inferred-why">
          This question is <strong>inferred</strong>: its wording was reconstructed rather than taken
          from a source form, and it is under review. Answer it as you read it.
        </p>
      )}

      <PrefillBadge entry={entry} onRevert={onRevert} />
      {/* Only shown when the field is not itself prefilled, so the two can never be confused. */}
      {entry?.prefill === undefined && <ReferenceNote reference={reference} />}

      {field.help !== undefined && (
        <p id={`${field.id}-help`} className="fx-help">
          {field.help}
        </p>
      )}

      {control}

      {/*
        `role="alert"` GOES ON A WRAPPER, NOT ON THE `<ul>`.
        ==================================================
        Found by axe in `test/dom/form-renderer.test.tsx`, not by reading: `role="alert"` REPLACES an
        element's implicit role, so `<ul role="alert">` is not a list, and its `<li>` children were
        orphaned list items (axe `listitem`, serious). The practical cost is that a screen reader stops
        saying "list, 3 items" — on the one control whose whole purpose is to enumerate what is wrong
        with an answer, so a technician heard three sentences with no indication of how many there were.

        Two elements is the fix that keeps both facts: the wrapper is the alert (and the
        `aria-describedby` target), the inner list is a list. `role="list"` is stated explicitly
        because `.fx-errors` sets `list-style: none`, which Safari treats as a reason to drop list
        semantics — the same defect arriving from the CSS side.
      */}
      {invalid && (
        <div id={`${field.id}-error`} className="fx-errors" role="alert">
          <ul role="list">
            {problems.map((p) => (
              <li key={`${p.kind}-${p.message}`}>{p.message}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
